import { create } from 'zustand';
import { notificationService, Notification } from '../services/notificationService';
import { captureError } from '../lib/errorHandler';

interface NotificationState {
  notifications: Notification[];
  unreadCount: number;
  isLoading: boolean;
  hasMore: boolean;
  currentPage: number;
  error: string | null;
  fcmToken: string | null;

  // Actions
  fetchNotifications: (page?: number) => Promise<void>;
  loadMore: () => Promise<void>;
  fetchUnreadCount: () => Promise<void>;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  addNotification: (notification: Notification) => void;
  setFcmToken: (token: string | null) => void;
  reset: () => void;
}

export const useNotificationStore = create<NotificationState>((set, get) => ({
  notifications: [],
  unreadCount: 0,
  isLoading: false,
  hasMore: false,
  currentPage: 1,
  error: null,
  fcmToken: null,

  fetchNotifications: async (page = 1) => {
    set({ isLoading: true, error: null });
    try {
      const result = await notificationService.getNotifications(page, 20);
      set({
        notifications: result.data,
        hasMore: result.hasNextPage,
        currentPage: result.page,
        isLoading: false,
      });
    } catch (error: any) {
      captureError(error, 'NotificationStore.fetchNotifications');
      set({
        isLoading: false,
        error: error.response?.data?.message || 'Failed to fetch notifications',
      });
    }
  },

  loadMore: async () => {
    const { hasMore, isLoading, currentPage } = get();
    if (!hasMore || isLoading) return;

    set({ isLoading: true });
    try {
      const nextPage = currentPage + 1;
      const result = await notificationService.getNotifications(nextPage, 20);
      set((state) => ({
        notifications: [...state.notifications, ...result.data],
        hasMore: result.hasNextPage,
        currentPage: result.page,
        isLoading: false,
      }));
    } catch (error: any) {
      captureError(error, 'NotificationStore.loadMore');
      set({ isLoading: false });
    }
  },

  fetchUnreadCount: async () => {
    try {
      const count = await notificationService.getUnreadCount();
      set({ unreadCount: count });
    } catch {
      // Silently fail — badge count is non-critical
    }
  },

  markAsRead: async (id: string) => {
    try {
      await notificationService.markAsRead(id);
      set((state) => ({
        notifications: state.notifications.map((n) =>
          n.id === id ? { ...n, isRead: true, readAt: new Date().toISOString() } : n,
        ),
        unreadCount: Math.max(0, state.unreadCount - 1),
      }));
    } catch {
      // Silently fail
    }
  },

  markAllAsRead: async () => {
    try {
      await notificationService.markAllAsRead();
      set((state) => ({
        notifications: state.notifications.map((n) => ({
          ...n,
          isRead: true,
          readAt: n.readAt || new Date().toISOString(),
        })),
        unreadCount: 0,
      }));
    } catch {
      // Silently fail
    }
  },

  addNotification: (notification: Notification) => {
    set((state) => ({
      notifications: [notification, ...state.notifications],
      unreadCount: state.unreadCount + 1,
    }));
  },

  setFcmToken: (token: string | null) => {
    set({ fcmToken: token });
  },

  reset: () => {
    set({
      notifications: [],
      unreadCount: 0,
      isLoading: false,
      hasMore: false,
      currentPage: 1,
      error: null,
      fcmToken: null,
    });
  },
}));
